import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { useCreateVisitType, useUpdateVisitType } from '@/hooks/useVisitTypes'
import { useProviderAuth } from '@/hooks/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { VisitType, VisitTypeCategory } from '@/types/settings'

const visitTypeSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  category: z.enum(['initial', 'follow_up', 'refill']),
  provider_id: z.string().min(1, 'Provider is required'),
  provider_name: z.string().min(1, 'Provider name is required'),
  price_dollars: z.string().min(1, 'Price is required'),
  description: z.string().optional(),
  is_active: z.boolean(),
})

type VisitTypeForm = z.infer<typeof visitTypeSchema>

interface VisitTypeModalProps {
  visitType: VisitType | null
  open: boolean
  onClose: () => void
  onSaved?: () => void
  showToasts?: boolean
}

export function VisitTypeModal({
  visitType,
  open,
  onClose,
  onSaved,
  showToasts = true,
}: VisitTypeModalProps) {
  const { provider } = useProviderAuth()
  const createType = useCreateVisitType()
  const updateType = useUpdateVisitType()

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<VisitTypeForm>({
    resolver: zodResolver(visitTypeSchema),
    defaultValues: visitType
      ? {
          name: visitType.name,
          category: visitType.category,
          provider_id: visitType.provider_id,
          provider_name: visitType.provider_name,
          price_dollars: (visitType.price_cents / 100).toFixed(2),
          description: visitType.description,
          is_active: visitType.is_active,
        }
      : {
          name: '',
          category: 'initial' as VisitTypeCategory,
          provider_id: provider?.id ?? '',
          provider_name: provider?.name ?? '',
          price_dollars: '',
          description: '',
          is_active: true,
        },
  })

  useEffect(() => {
    if (visitType) {
      reset({
        name: visitType.name,
        category: visitType.category,
        provider_id: visitType.provider_id,
        provider_name: visitType.provider_name,
        price_dollars: (visitType.price_cents / 100).toFixed(2),
        description: visitType.description,
        is_active: visitType.is_active,
      })
    } else {
      reset({
        name: '',
        category: 'initial' as VisitTypeCategory,
        provider_id: provider?.id ?? '',
        provider_name: provider?.name ?? '',
        price_dollars: '',
        description: '',
        is_active: true,
      })
    }
  }, [visitType, reset, provider])

  const category = watch('category')
  const isActive = watch('is_active')

  const onSubmit = async (data: VisitTypeForm) => {
    const payload = {
      name: data.name,
      category: data.category,
      provider_id: data.provider_id,
      provider_name: data.provider_name,
      price_cents: Math.round(parseFloat(data.price_dollars) * 100),
      description: data.description ?? '',
      is_active: data.is_active,
    }

    try {
      if (visitType) {
        await updateType.mutateAsync({ id: visitType.id, data: payload })
        if (showToasts) toast.success('Visit type updated')
      } else {
        await createType.mutateAsync(payload)
        if (showToasts) toast.success('Visit type created')
      }
      reset()
      onSaved?.()
      onClose()
    } catch {
      if (showToasts) toast.error('Failed to save visit type')
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{visitType ? 'Edit Visit Type' : 'Add Visit Type'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="vt-name">Name</Label>
            <Input id="vt-name" {...register('name')} />
            {errors.name && <p className="text-sm text-red-600">{errors.name.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={category}
                onValueChange={(val) =>
                  setValue('category', val as VisitTypeCategory, { shouldValidate: true })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="initial">Initial</SelectItem>
                  <SelectItem value="follow_up">Follow-Up</SelectItem>
                  <SelectItem value="refill">Refill</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="vt-price">Price ($)</Label>
              <Input id="vt-price" type="number" step="0.01" min="0" {...register('price_dollars')} />
              {errors.price_dollars && (
                <p className="text-sm text-red-600">{errors.price_dollars.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="vt-description">Description</Label>
            <Textarea id="vt-description" rows={3} {...register('description')} />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="vt-active"
              checked={isActive}
              onChange={(e) => setValue('is_active', e.target.checked)}
              className="h-4 w-4 rounded border-slate-300"
            />
            <Label htmlFor="vt-active">Active</Label>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : visitType ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
